import {useEffect, useMemo} from 'react';

import {mapCompanyWorkforceProfilesResponse} from '../serializers/WorkforceProfileSerializers';
import type {CompanyWorkforceProfile} from '../types/WorkforceProfileTypes';
import {isProfileComparisonEligible} from '../utils/ProfileComparison/filterProfileComparisonProfiles';

import {useQueryPagination, PaginationResponse} from '../../../../hooks/useQueryPagination';
import {GetRoutes} from '../../../../lib/api/GET/GetRoutes';

const mapResponse = (raw: unknown): PaginationResponse<CompanyWorkforceProfile, Record<string, never>> => {
  const result = mapCompanyWorkforceProfilesResponse(raw);
  return {
    data: result.data,
    pagination: result.pagination,
  };
};

export const useProfileComparisonProfiles = (companyId: string, departmentId: string | null) => {
  const enabled = !!companyId && !!departmentId;

  const {data, isLoading, error, hasNext, getNext} = useQueryPagination<
    CompanyWorkforceProfile,
    Record<string, never>
  >({
    url: GetRoutes.companyWorkforceProfiles(companyId),
    entity: 'companyWorkforceProfiles',
    enabled,
    mapResponse,
  });

  useEffect(() => {
    if (enabled && hasNext && !isLoading) {
      getNext();
    }
  }, [enabled, getNext, hasNext, isLoading]);

  const departmentProfiles = useMemo(() => {
    if (!departmentId) {
      return [];
    }

    return data.filter(
      (profile) => profile.companyDepartmentId === departmentId && isProfileComparisonEligible(profile)
    );
  }, [data, departmentId]);

  return {
    profiles: departmentProfiles,
    isLoading: enabled && (isLoading || hasNext),
    error,
  };
};
